/*
 * Copyright (c) 2025, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'

// Chakra Components
import {Stack, Divider} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import ProductItem from '../product-item'

/**
 * Component that renders a list of product items with consistent props and behavior.
 * Extracted from cart page to be reusable across different contexts.
 */
const ProductItemList = ({
    productItems = [],
    index,
    totalRegularProducts,
    oosBopisSkus = [],
    globaleUnavailableSKUs,
    showPriceSkeleton,
    currencySymbol,
    totalBonusProducts,
    isBonus = false,
    renderSecondaryActions,
    renderDeliveryActions,
    onItemQuantityChange,
    outOfStockProductsList = [],
    onRemoveItemClick,
    // Optional props with defaults
    productsByItemId = {},
    isProductsLoading = false,
    localQuantity = {},
    localIsGiftItems = {},
    isCartItemLoading = false,
    selectedItem = null,
    removingItemIds = [],
    // Styling options
    hideBorder = false,
    hideBottomBorder = false,
    // Pickup information
    getShipmentInfoForProduct = null,
    // Checkout: read-only bag, different layout
    isCheckout = false
}) => {
    return (
        <Stack spacing={0} className="product-items-stack">
            {productItems
                ?.filter(({c_hiddenFluidProduct}) => !c_hiddenFluidProduct)
                .map((productItem) => {
                    const isBonusProductItem = productItem.bonusProductLineItem
                    // Check if this product item (regular or bonus) is being removed
                    const isBeingRemoved = removingItemIds.includes(productItem.itemId)

                    // Get pickup information for this product item
                    const shipmentInfo = getShipmentInfoForProduct
                        ? getShipmentInfoForProduct(productItem)
                        : null
                    const pickupInStore = shipmentInfo?.isPickupOrder || false
                    const showDivider =
                        (index !== 0 && !isBonus) ||
                        (index === 0 && !isBonus && totalBonusProducts) ||
                        (index !== 0 && isBonus) ||
                        (index !== 0 && index < totalRegularProducts && !isBonus)
                    return (
                        <React.Fragment key={productItem.itemId}>
                            {showDivider && (
                                <Divider
                                    borderColor="gray.700"
                                    margin="30px 0px 30px 0px"
                                    className="divider-line"
                                    orientation="horizontal"
                                />
                            )}
                            <ProductItem
                                currencySymbol={currencySymbol}
                                showPriceSkeleton={showPriceSkeleton}
                                globaleUnavailableSKUs={globaleUnavailableSKUs}
                                isBonusProduct={isBonusProductItem}
                                isRemoving={isBeingRemoved}
                                pickupInStore={pickupInStore}
                                oosBopisSkus={oosBopisSkus}
                                outOfStockProductsList={outOfStockProductsList}
                                containerStyles={{
                                    borderX: 'none',
                                    borderTop: 'none',
                                    boxShadow: 'none',
                                    ...(hideBottomBorder && {borderBottom: 'none'})
                                }}
                                secondaryActions={
                                    !isCheckout && renderSecondaryActions
                                        ? renderSecondaryActions({
                                              productItem,
                                              isAGift: localIsGiftItems[productItem.itemId]
                                                  ? localIsGiftItems[productItem.itemId]
                                                  : productItem.gift
                                          })
                                        : null
                                }
                                deliveryActions={
                                    !isCheckout && renderDeliveryActions
                                        ? renderDeliveryActions(productItem)
                                        : null
                                }
                                product={{
                                    ...productItem,
                                    ...(productsByItemId && productsByItemId[productItem.itemId]),
                                    isProductUnavailable: !isProductsLoading
                                        ? !productsByItemId?.[productItem.itemId]
                                        : undefined,
                                    price: productItem.price,
                                    quantity: localQuantity[productItem.itemId]
                                        ? localQuantity[productItem.itemId]
                                        : productItem.quantity
                                }}
                                onItemQuantityChange={onItemQuantityChange?.bind(this, productItem)}
                                showLoading={
                                    (isCartItemLoading &&
                                        selectedItem?.itemId === productItem.itemId) ||
                                    isBeingRemoved
                                }
                                handleRemoveItem={isCheckout ? undefined : onRemoveItemClick}
                                hideBorder={hideBorder}
                                productItem={productItem}
                                isCheckout={isCheckout}
                            />
                        </React.Fragment>
                    )
                })}
        </Stack>
    )
}

ProductItemList.propTypes = {
    productItems: PropTypes.arrayOf(PropTypes.object),
    renderSecondaryActions: PropTypes.func,
    renderDeliveryActions: PropTypes.func,
    onItemQuantityChange: PropTypes.func.isRequired,
    onRemoveItemClick: PropTypes.func,
    productsByItemId: PropTypes.object,
    showPriceSkeleton: PropTypes.bool,
    currencySymbol: PropTypes.string,
    isProductsLoading: PropTypes.bool,
    outOfStockProductsList: PropTypes.array,
    localQuantity: PropTypes.object,
    localIsGiftItems: PropTypes.object,
    isCartItemLoading: PropTypes.bool,
    selectedItem: PropTypes.object,
    removingItemIds: PropTypes.arrayOf(PropTypes.string),
    hideBorder: PropTypes.bool,
    hideBottomBorder: PropTypes.bool,
    getShipmentInfoForProduct: PropTypes.func,
    isCheckout: PropTypes.bool,
    isBonus: PropTypes.bool,
    totalRegularProducts: PropTypes.number,
    totalBonusProducts: PropTypes.number,
    giftCertificateItems: PropTypes.array,
    globaleUnavailableSKUs: PropTypes.array,
    oosBopisSkus: PropTypes.array,
    index: PropTypes.number
}

export default ProductItemList
